import {
  Badge,
  Box,
  Divider,
  Group,
  Loader,
  Paper,
  SimpleGrid,
  Stack,
  Text,
  Title,
} from "@mantine/core";
import { LoaderFunctionArgs, MetaFunction, json } from "@remix-run/node";
import { Link, useLoaderData, useNavigation } from "@remix-run/react";

import { BusinessReviewBadge } from "~/components/business-review-badge";
import {
  DEFAULT_OG_IMAGE_EN,
  DEFAULT_OG_IMAGE_FR,
  PAGE_TITLE,
} from "~/constants";
import { specialtyLocationPages } from "~/data/specialty-location-pages";
import { useTranslation } from "~/hooks/use-typed-translation";
import i18n from "~/localization/i18n";
import i18nextServer from "~/localization/i18n.server";
import utilityClasses from "~/styles/utilities.module.scss";
import { getCentralizedMeta } from "~/utils/meta-helper";

const getLocalized = <T,>(value: { fr: T; en: T }, locale: string) =>
  locale === "en" ? value.en : value.fr;

function SpecialtyCardLink({
  to,
  label,
  cityLabel,
}: {
  to: string;
  label: string;
  cityLabel: string;
}) {
  const navigation = useNavigation();
  const isLoading = navigation.state !== "idle" && navigation.location?.pathname === to;

  return (
    <Paper
      component={Link}
      to={to}
      withBorder
      p="md"
      radius="sm">
      <Group justify="space-between" align="center" wrap="nowrap">
        <div>
          <Text fw={600} fz={15}>
            {label}
          </Text>
          <Text c="dimmed" fz={13} mt={4}>
            {cityLabel}
          </Text>
        </div>
        {isLoading ? <Loader size="xs" /> : null}
      </Group>
    </Paper>
  );
}

export const meta: MetaFunction<typeof loader> = ({ data, location }) => {
  if (!data) return [];

  return getCentralizedMeta({
    title: data.title,
    description: data.description,
    image: data.locale === "fr" ? DEFAULT_OG_IMAGE_FR : DEFAULT_OG_IMAGE_EN,
    url: data.origin + location.pathname,
    locale: data.locale,
    keywords:
      "médecins Abidjan, médecins Dakar, médecins Douala, médecins Yaoundé, spécialités médicales, rendez-vous médical",
    alternateLanguages: {
      en: `${data.origin}/en/specialites`,
      fr: `${data.origin}/fr/specialites`,
    },
  });
};

export async function loader({ request, params }: LoaderFunctionArgs) {
  const lang = params.lang ?? "";
  const locale = i18n.supportedLngs.includes(lang.toLocaleLowerCase())
    ? lang
    : await i18nextServer.getLocale(request);
  const origin = new URL(request.url).origin;

  return json({
    locale,
    origin,
    title:
      locale === "fr"
        ? `Spécialités médicales en Afrique francophone | ${PAGE_TITLE}`
        : `Medical Specialties in Francophone Africa | ${PAGE_TITLE}`,
    description:
      locale === "fr"
        ? "Trouvez des médecins par spécialité à Abidjan, Dakar, Douala et Yaoundé avec La Ruche Health."
        : "Find doctors by specialty in Abidjan, Dakar, Douala, and Yaoundé with La Ruche Health.",
    pages: specialtyLocationPages.map(page => ({
      specialtySlug: page.specialtySlug,
      citySlug: page.citySlug,
      label: getLocalized(page.specialtyLabel, locale),
      cityLabel: page.cityLabel,
      countryCode: page.countryCode,
      countryLabel: getLocalized(page.countryLabel, locale),
    })),
  });
}

export default function SpecialtiesIndexRoute() {
  const { t } = useTranslation();
  const { locale, pages } = useLoaderData<typeof loader>();
  const groupedPages = pages.reduce<
    Array<{
      countryCode: string;
      countryLabel: string;
      cities: Array<{
        citySlug: string;
        cityLabel: string;
        pages: Array<(typeof pages)[number]>;
      }>;
    }>
  >((countries, page) => {
    let country = countries.find(item => item.countryCode === page.countryCode);

    if (!country) {
      country = {
        countryCode: page.countryCode,
        countryLabel: page.countryLabel,
        cities: [],
      };
      countries.push(country);
    }

    let city = country.cities.find(item => item.citySlug === page.citySlug);

    if (!city) {
      city = {
        citySlug: page.citySlug,
        cityLabel: page.cityLabel,
        pages: [],
      };
      country.cities.push(city);
    }

    city.pages.push(page);

    return countries;
  }, []);

  return (
    <main className={utilityClasses.container}>
      <Box py={{ base: "2.5rem", sm: "4rem" }} ta="center">
        <Badge variant="light" color="primary" mb="sm">
          Abidjan · Dakar · Douala · Yaoundé
        </Badge>
        <Title order={1} fw={700} fz={{ base: 28, sm: 38 }} lh={1.15}>
          {t("specialties-hub-title")}
        </Title>
        <Text mt="md" maw={720} mx="auto" fz={{ base: 15, sm: 17 }} c="dimmed" lh={1.6}>
          {t("specialties-hub-description")}
        </Text>
        <BusinessReviewBadge mt="md" />
      </Box>

      <Divider />

      <Box py={{ base: "2rem", sm: "3.25rem" }}>
        <Stack gap="xl">
          {groupedPages.map(country => (
            <section key={country.countryCode}>
              <Group justify="space-between" align="end" mb="lg">
                <div>
                  <Title order={2} fz={{ base: 22, sm: 28 }} fw={650}>
                    {country.countryLabel}
                  </Title>
                  <Text mt={4} c="dimmed" fz={14}>
                    {country.cities.map(city => city.cityLabel).join(", ")}
                  </Text>
                </div>
              </Group>

              <Stack gap="lg">
                {country.cities.map(city => (
                  <section key={city.citySlug}>
                    <Title order={3} fz={{ base: 18, sm: 21 }} fw={650} mb="sm">
                      {city.cityLabel}
                    </Title>
                    <SimpleGrid cols={{ base: 1, sm: 2, md: 4 }} spacing="sm">
                      {city.pages.map(page => (
                        <SpecialtyCardLink
                          key={`${page.citySlug}-${page.specialtySlug}`}
                          to={`/${locale}/specialites/${page.specialtySlug}/${page.citySlug}`}
                          label={page.label}
                          cityLabel={page.cityLabel}
                        />
                      ))}
                    </SimpleGrid>
                  </section>
                ))}
              </Stack>
            </section>
          ))}
        </Stack>
      </Box>
    </main>
  );
}
